import { syncRule, validity } from '../../../common/decoration/decorators';
import { buildControlIdFromParent, descriptionSrcURL } from '../../../common/decoration';
import { getAppSettingPathV2 } from '../../application';
import { convertEnumToCSV } from '../../../common';
import type { v2 } from '@sap/ux-specification-types';
import { ControlType, DATESETTINGSPATH } from '@sap/ux-specification-types';

/**
 * Imports date settings from the provided manifest section.
 *
 * @param manifestSection - The manifest section containing date settings.
 * @returns The imported date settings as a DateRange object or undefined if not available.
 */
export function importDateSettings(manifestSection: object): DateRange {
    if (!manifestSection || !manifestSection['dateSettings']) {
        return undefined;
    }
    const configPart: DateRange = JSON.parse(JSON.stringify(manifestSection['dateSettings']));
    // converted CSV format to array of strings
    if (typeof manifestSection['dateSettings']['selectedValues'] === 'string') {
        configPart.selectedValues = manifestSection['dateSettings']['selectedValues'].split(',') as v2.DateRangeType[];
    }
    delete configPart['fields'];
    return configPart;
}

/**
 * Get specific fields from the date settings in the manifest section.
 *
 * @param manifestSection - The manifest section containing the date settings.
 * @param _sectionId - The section ID (currently unused, required as this function is assigned to the sync rule see interface ManifestConverter).
 * @param propertyKey - The key of the property to import.
 * @returns The field value or undefined if not available.
 */
function importDateSettingsField(manifestSection: string | Array<object>, _sectionId: string, propertyKey: string) {
    const configPart = manifestSection?.[propertyKey]
        ? JSON.parse(JSON.stringify(manifestSection?.[propertyKey]))
        : undefined;
    let selectionField;
    if (propertyKey === 'selectedValues' && typeof configPart === 'string') {
        selectionField = configPart.split(',') as v2.DateRangeType[];
    }
    if (propertyKey === 'filter' && typeof configPart === 'object') {
        selectionField = [];
        configPart.forEach((section, index) => {
            selectionField[index] = section;
            if (section['equals'] && typeof section['equals'] === 'string') {
                selectionField[index].equals = section['equals'].split(',') as v2.DateRangeType[];
            }
        });
    }
    return selectionField;
}

/**
 * Exports date settings to the manifest section.
 *
 * @param manifestSection - The manifest section to which the field will be exported.
 * @param configPart - The configuration part containing the field data to export.
 * @param key - The key of the field to export.
 */
export function exportDateSettingsField(manifestSection: unknown, configPart: DateRange, key: string): void {
    manifestSection[key] = JSON.parse(JSON.stringify(configPart[key]));
    const field = configPart[key];
    if (key === 'selectedValues') {
        manifestSection[key] = field.join();
    }
    if (key === 'filter') {
        for (const filterKey in field) {
            const filterValue = field[filterKey];
            manifestSection[key][filterKey] = filterValue;
            if (filterValue.equals && Array.isArray(filterValue.equals)) {
                manifestSection[key][filterKey].equals = filterValue.equals.join();
            }
        }
    }
}

export class DateRange implements v2.DateRange {
    @syncRule({
        manifest: {
            path: (pageKeys): string => {
                return getAppSettingPathV2(pageKeys) + DATESETTINGSPATH;
            }
        }
    })
    @validity({
        since: '1.77.0'
    })
    useDateRange?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + DATESETTINGSPATH,
            import: importDateSettings,
            export: convertEnumToCSV
        }
    })
    @validity({
        since: '1.77.0'
    })
    selectedValues?: v2.DateRangeType[];

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + DATESETTINGSPATH
        }
    })
    @validity({
        since: '1.77.0'
    })
    exclude?: boolean;
}

const flexRuleForSmartFilterBar = {
    controlId: buildControlIdFromParent,
    controlType: (): ControlType => ControlType.SmartFilterBar
};

const manifestRuleForSelectionField = {
    path: (pageKeys, _manifest, breadcrumbs): string =>
        `${getAppSettingPathV2(pageKeys)}${DATESETTINGSPATH}/fields/${breadcrumbs}`
};

const manifestRuleForSelectionFieldWithArrays = {
    ...manifestRuleForSelectionField,
    import: importDateSettingsField,
    export: exportDateSettingsField
};

export class DefaultDateRange implements v2.DefaultDateRange {
    @syncRule({
        manifest: manifestRuleForSelectionField
    })
    @validity({
        since: '1.90.0'
    })
    operation: v2.DefaultDateRangeValueType;
}

export class SelectionFieldDateSettings implements v2.SelectionFieldDateSettings {
    @syncRule({
        manifest: manifestRuleForSelectionFieldWithArrays
    })
    @validity({
        since: '1.77.0'
    })
    selectedValues?: v2.DateRangeType[];

    @syncRule({
        manifest: manifestRuleForSelectionField
    })
    @validity({
        since: '1.77.0'
    })
    exclude?: boolean;

    @syncRule({
        manifest: manifestRuleForSelectionField
    })
    @validity({
        since: '1.77.0'
    })
    customDateRangeImplementation?: string;

    @syncRule({
        manifest: manifestRuleForSelectionFieldWithArrays
    })
    @validity({
        since: '1.77.0'
    })
    filter?: v2.FilterType[];

    @syncRule({
        manifest: manifestRuleForSelectionField
    })
    @validity({
        since: '1.90.0'
    })
    defaultValue?: DefaultDateRange;
}

export class SelectionFields implements v2.SelectionFields {
    [key: string]: v2.SelectionField | v2.SelectionFieldDateSettings;
}

export class CommonFilterBar implements v2.CommonFilterBar {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.38.0'
    })
    smartVariantManagement?: boolean;

    @syncRule({
        flex: flexRuleForSmartFilterBar
    })
    @validity({
        since: '1.40.0'
    })
    liveMode?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.48.0'
    })
    variantManagementHidden: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.52.0' // Available in 1.52 so exact validity information is not required
    })
    subTitleIfVariantMgmtHidden?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + '/filterSettings',
            import: importDateSettings
        }
    })
    @validity({
        since: '1.77.0'
    })
    dateSettings?: DateRange;

    selectionFields?: SelectionFields;

    @syncRule({
        flex: flexRuleForSmartFilterBar
    })
    @validity({
        since: '1.28.0'
    })
    showClearOnFB?: boolean;

    @syncRule({
        flex: flexRuleForSmartFilterBar
    })
    @validity({
        since: '1.38.0'
    })
    showFilterConfiguration?: boolean;

    @syncRule({
        flex: flexRuleForSmartFilterBar
    })
    @validity({
        since: '1.28.0'
    })
    showRestoreOnFB?: boolean;
}

export class AnalyticalListPageFilterBar extends CommonFilterBar implements v2.AnalyticalListPageFilterBar {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    showGoButtonOnFilterBar?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.60.0'
    })
    filterDefaultsFromSelectionVariant?: boolean;
}

export class ListReportFilterBar extends CommonFilterBar implements v2.ListReportFilterBar {
    // This is replaced by an enum containing list of SelectionVariant, PresentationVariant, and SelectionPresentationVariant annotations during generate
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/49a6ba5b8d6946208322a9f7e16837c2')
    @validity({
        since: '1.86.0'
    })
    annotationPath?: string;
}
